'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  AIBotConfig,
  AIBotSession,
  BotMessage,
  BotMessageType,
  BotInfo,
  SessionType,
  RegisterBotSessionRequest,
  SendBotMessageRequest,
  BotSessionEstablishedResponse,
  BotSwitchedNotification,
  AIBotError as _AIBotError
} from '@/types/ai-bot-framework';
import { useAIBotProvider } from '@/contexts/ai-bot-context';
import { getCurrentPageContext } from '@/utils/page-context-parser';

/**
 * Core hook for AI Bot Framework - provides explicit session management and communication
 */
export function useAIBot(config: AIBotConfig): AIBotSession {
  // Debug logging removed to reduce noise
  
  // Generate stable session ID
  const sessionId = useRef(generateSessionId()).current;

  // State management
  const [isRegistered, setIsRegistered] = useState(false);
  const [currentBot, setCurrentBot] = useState<BotInfo | null>(null);
  const [availableBots, setAvailableBots] = useState<BotInfo[]>([]);
  const [messages, setMessages] = useState<BotMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const processingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  
  // Get SignalR connection from provider
  const { connection, isConnected, registerMessageHandler, unregisterMessageHandler } = useAIBotProvider();
  
  // Refs for cleanup and state management
  const registrationPromiseRef = useRef<Promise<void> | null>(null);
  const isUnmountingRef = useRef(false);
  const configRef = useRef(config);
  const initialMessageSentRef = useRef(false);
  
  // Ensure unmounting flag starts as false
  useEffect(() => {
    isUnmountingRef.current = false;
  }, []);
  
  // Update config ref when config changes
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Generate unique session ID
  function generateSessionId(): string {
    return `${config.sessionType.toLowerCase()}-${Date.now()}-${uuidv4().substring(0, 8)}`;
  }

  // Message handlers
  const handleBotMessage = useCallback((message: BotMessage) => {
    // 🚨 IMMEDIATE ACTIONBUTTON DETECTION
    if (message.metadata?.actionButton) {
      console.error('🚨🚨🚨 ACTIONBUTTON RECEIVED 🚨🚨🚨');
      console.error('ActionButton data:', message.metadata.actionButton);
      console.error('Full message:', message);
    }

    console.log('[useAIBot] 📨 Message received:', {
      sessionId: message.sessionId,
      messageType: message.messageType,
      hasContent: !!message.content,
      hasMetadata: !!message.metadata,
      metadataKeys: message.metadata ? Object.keys(message.metadata) : [],
      fullMetadata: message.metadata
    });

    if (message.sessionId !== sessionId) {
      console.log('[useAIBot] ⚠️ Session mismatch, ignoring');
      return; // Session isolation
    }

    // Call custom message handler if provided
    if (configRef.current.onMessage) {
      try {
        configRef.current.onMessage(message);
      } catch (err) {
        console.error('Error in custom message handler:', err);
      }
    }

    // Handle system messages
    if (message.messageType === 'System' && message.isComplete) {
      setIsProcessing(false);
      return;
    }

    // Handle error messages
    if (message.isError) {
      setError(message.errorMessage || 'An error occurred');
      setIsProcessing(false);

      // Clear the timeout on error
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = undefined;
      }
      return;
    }

    // For visible chats, update messages state
    if (configRef.current.sessionType === SessionType.Visible) {
      console.log('[useAIBot] 💾 Adding to messages array');
      setMessages(prev => {
        console.log('[useAIBot] 📊 Current messages count:', prev.length);

        // Find existing message or create new one
        const existingIndex = prev.findIndex(m => m.requestId === message.requestId);

        if (existingIndex >= 0) {
          // Update existing message
          console.log('[useAIBot] 🔄 Updating existing message');
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            content: (updated[existingIndex].content || '') + (message.content || ''),
            isComplete: message.isComplete,
            timestamp: message.timestamp,
            metadata: message.metadata // Preserve metadata!
          };
          console.log('[useAIBot] ✅ Updated messages count:', updated.length);
          return updated;
        } else if (message.content || message.messageType !== 'System') {
          // Add new message
          console.log('[useAIBot] ➕ Adding new message');
          const updated = [...prev, message];
          console.log('[useAIBot] ✅ New messages count:', updated.length);
          return updated;
        }

        console.log('[useAIBot] ⏭️ Skipping message (no content, is System)');
        return prev;
      });
    } else {
      console.log('[useAIBot] ⏭️ Not a visible session, skipping messages array update');
    }

    // Update processing state
    if (message.isComplete) {
      // Message marked as complete, stopping processing spinner
      setIsProcessing(false);
      
      // Clear the timeout since we completed normally
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = undefined;
      }
    } else if (message.messageType === 'TextMessage' && !message.content) {
      // Sometimes the final message might not have isComplete flag but has no content
      // Empty text message received, might be end of stream
    }
  }, [sessionId]);

  
  const handleSessionEstablished = useCallback((response: BotSessionEstablishedResponse) => {
    // Session establishment logging removed
    
    if (response.sessionId !== sessionId) {
      // Session ID mismatch - ignoring
      return;
    }
    
    // Session established for our session
    setCurrentBot(response.assignedBot);
    setIsRegistered(true);
    setError(null);
    
    if (!response.requestedBotAvailable && response.fallbackReason) {
      console.warn('Requested bot not available:', response.fallbackReason);
    }
  }, [sessionId]);

  const handleBotSwitched = useCallback((notification: BotSwitchedNotification) => {
    if (notification.sessionId !== sessionId) return;
    
    // Bot switched
    setCurrentBot(notification.newBot);
    
    // Add system message about bot switch for visible chats
    if (configRef.current.sessionType === SessionType.Visible) {
      const switchMessage: BotMessage = {
        sessionId,
        requestId: `switch-${Date.now()}`,
        botId: 'system',
        botName: 'System',
        messageType: BotMessageType.System,
        content: `Bot switched to ${notification.newBot.botName}: ${notification.reason}`,
        metadata: {},
        isComplete: true,
        isError: false,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, switchMessage]);
    }
  }, [sessionId]);

  const handleSessionTerminated = useCallback((terminatedSessionId: string, reason: string) => {
    if (terminatedSessionId !== sessionId) return;

    // Session terminated
    setError(`Session terminated: ${reason}`);
    setIsRegistered(false);
    setCurrentBot(null);
    setIsProcessing(false);
  }, [sessionId]);

  const handleAvailableBotsUpdated = useCallback((data: { sessionId: string; availableBots: BotInfo[] }) => {
    if (data.sessionId !== sessionId) return;

    setAvailableBots(data.availableBots);
  }, [sessionId]);

  // Track if handlers are registered
  const [handlersRegistered, setHandlersRegistered] = useState(false);

  // Register message handlers with connection
  useEffect(() => {
    if (!connection || !isConnected) {
      setHandlersRegistered(false);
      return;
    }

    const handlers = {
      ReceiveBotMessage: handleBotMessage,
      BotSessionEstablished: handleSessionEstablished,
      BotSwitched: handleBotSwitched,
      SessionTerminated: handleSessionTerminated,
      AvailableBotsUpdated: handleAvailableBotsUpdated
    };

    // Registering SignalR handlers
    
    // Register handlers
    Object.entries(handlers).forEach(([eventName, handler]) => {
      registerMessageHandler(eventName, handler);
    });
    
    setHandlersRegistered(true);

    return () => {
      // Unregistering SignalR handlers
      // Unregister handlers
      Object.keys(handlers).forEach(eventName => {
        unregisterMessageHandler(eventName);
      });
      setHandlersRegistered(false);
    };
  }, [
    connection,
    isConnected,
    handleBotMessage,
    handleSessionEstablished,
    handleBotSwitched,
    handleSessionTerminated,
    handleAvailableBotsUpdated,
    registerMessageHandler,
    unregisterMessageHandler,
    sessionId
  ]);

  // Register session when connected
  const registerSession = useCallback(async (): Promise<void> => {
    if (!connection || !isConnected || !handlersRegistered || isRegistered || registrationPromiseRef.current) {
      if (!handlersRegistered) {
        // Waiting for handlers to be registered before session registration
      }
      return registrationPromiseRef.current || Promise.resolve();
    }

    // Registering bot session

    const registrationPromise = (async () => {
      try {
        // Get fresh pageContext directly from URL
        const pageContext = getCurrentPageContext();

        const request: RegisterBotSessionRequest = {
          sessionId,
          requestedBotId: config.requestedBotId,
          sessionType: config.sessionType,
          pageContext
          // Note: Don't send initialMessage here - we'll send it through normal flow after registration
          // Note: TenantId and UserId are derived from auth context on server side
        };

        // Invoking RegisterBotSession
        await connection.invoke('RegisterBotSession', request);
        // Bot session registration sent successfully
        
        // Wait a bit to see if we get the response
        await new Promise(resolve => setTimeout(resolve, 100));
        // Registration status updated
      } catch (err) {
        console.error('Failed to register bot session:', err);
        setError('Failed to register bot session');
        throw err;
      } finally {
        registrationPromiseRef.current = null;
      }
    })();

    registrationPromiseRef.current = registrationPromise;
    return registrationPromise;
  }, [connection, isConnected, isRegistered, sessionId, config, handlersRegistered]);

  // Auto-register when connected and handlers are ready
  useEffect(() => {
    // Check if we should auto-register
    const shouldRegister = isConnected && handlersRegistered && !isRegistered && !isUnmountingRef.current;
    
    // Auto-registration check
    
    if (shouldRegister) {
      // Auto-registering session - all conditions met
      registerSession().catch(err => {
        console.error('Auto-registration failed:', err);
      });
    }
  }, [isConnected, handlersRegistered, isRegistered, registerSession]);

  // Send message function
  const sendMessage = useCallback(async (content: string, metadata?: Record<string, any>): Promise<void> => {
    if (!connection || !isConnected || !isRegistered) {
      throw new Error('Not connected or registered');
    }

    if (!content.trim()) {
      throw new Error('Message content cannot be empty');
    }

    const requestId = `${sessionId}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    
    // Sending bot message

    // Add user message to visible chats
    if (config.sessionType === SessionType.Visible) {
      const userMessage: BotMessage = {
        sessionId,
        requestId: `user-${requestId}`,
        botId: 'user',
        botName: 'You',
        messageType: BotMessageType.TextMessage,
        content,
        metadata: {},
        isComplete: true,
        isError: false,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMessage]);
    }

    setIsProcessing(true);
    setError(null);

    // Clear any existing timeout
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }

    // Set a timeout to stop processing after 30 seconds as a fallback
    processingTimeoutRef.current = setTimeout(() => {
      console.warn('Processing timeout reached, stopping spinner');
      setIsProcessing(false);
    }, 30000);

    try {
      // Get fresh pageContext directly from URL - always current!
      const pageContext = getCurrentPageContext();

      const request: SendBotMessageRequest = {
        sessionId,
        content,
        requestId,
        metadata: metadata || {},
        pageContext  // Include current page context with every message
      };

      await connection.invoke('SendBotMessage', request);
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message');
      setIsProcessing(false);
      throw err;
    }
  }, [connection, isConnected, isRegistered, sessionId, config.sessionType]);

  // Switch to a different bot
  const switchBot = useCallback(async (newBotId: string): Promise<void> => {
    if (!connection || !isConnected || !isRegistered) {
      throw new Error('Not connected or registered');
    }

    if (!newBotId) {
      throw new Error('Bot ID is required');
    }

    try {
      await connection.invoke('SwitchBotInSession', sessionId, newBotId);
      // The server will send back a BotSwitched notification which will update currentBot
    } catch (err) {
      console.error('Failed to switch bot:', err);
      setError('Failed to switch bot');
      throw err;
    }
  }, [connection, isConnected, isRegistered, sessionId]);

  // Unregister session
  const unregisterSession = useCallback(async (): Promise<void> => {
    // Only unregister if we have a connection, session ID, and the session was actually registered
    if (!connection || !sessionId || !isRegistered) return;

    // Unregistering bot session

    try {
      if (isConnected) {
        await connection.invoke('UnregisterBotSession', sessionId);
      }
      setIsRegistered(false);
      setCurrentBot(null);
      setIsProcessing(false);
    } catch (err) {
      console.error('Failed to unregister session:', err);
    }
  }, [connection, isConnected, sessionId, isRegistered]);

  // Clear messages (for visible chats)
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  // Reconnect function
  const reconnect = useCallback(async (): Promise<void> => {
    setError(null);
    setIsRegistered(false);
    setCurrentBot(null);
    setIsProcessing(false);
    
    if (isConnected) {
      await registerSession();
    }
  }, [isConnected, registerSession]);

  // Auto-send initial message after registration
  useEffect(() => {
    // Check if we should send the initial message
    if (!isRegistered || !config.initialMessage || initialMessageSentRef.current || isProcessing) {
      return;
    }
    
    // Use a guard to prevent double-execution in StrictMode
    let cancelled = false;
    
    // Preparing to auto-send initial message
    
    // Small delay to ensure UI is ready and to avoid StrictMode double-fire
    const timer = setTimeout(() => {
      if (cancelled) {
        // Initial message send cancelled (StrictMode cleanup)
        return;
      }
      
      // Double-check the ref hasn't been set by another execution
      if (initialMessageSentRef.current) {
        // Initial message already sent by another execution
        return;
      }
      
      // Mark as sent immediately before async operation
      initialMessageSentRef.current = true;
      // Auto-sending initial message now
      
      // Send the initial message using the normal flow
      sendMessage(config.initialMessage!, { isInitialMessage: true })
        .then(() => {
          // Initial message sent successfully
        })
        .catch(err => {
          console.error('Failed to send initial message:', err);
          // Reset so it can be retried
          if (!cancelled) {
            initialMessageSentRef.current = false;
          }
        });
    }, 200); // Slightly longer delay to let StrictMode settle
    
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isRegistered, config.initialMessage, sendMessage, isProcessing]);

  // Cleanup on unmount
  useEffect(() => {
    // Reset flags when component mounts
    isUnmountingRef.current = false;
    initialMessageSentRef.current = false;
    
    return () => {
      isUnmountingRef.current = true;
      initialMessageSentRef.current = false;
      unregisterSession().catch(err => {
        console.error('Cleanup unregister failed:', err);
      });
    };
  }, [unregisterSession]);

  return {
    sessionId,
    isConnected,
    isRegistered,
    currentBot,
    availableBots,
    messages,
    isProcessing,
    error,
    sendMessage,
    switchBot,
    registerSession,
    unregisterSession,
    clearMessages,
    reconnect
  };
}